// PermitsMap — MapLibre overlay for the Permits hub.
// Renders permits as colored pins by status. Click → opens detail.
// Lines for segment-anchored permits, circles for everything else.

import { useEffect, useMemo, useRef, useState } from 'react';
import maplibregl from 'maplibre-gl';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import type { Permit } from '../pages/PermitsPage';

const STATUS_COLOR: Record<string, string> = {
  needed:     '#ef4444',
  applied:    '#f59e0b',
  in_review:  '#3b82f6',
  approved:   '#10b981',
  issued:     '#059669',
  expired:    '#7f1d1d',
  revoked:    '#7f1d1d',
  superseded: '#94a3b8',
  denied:     '#7f1d1d',
  waived:     '#94a3b8',
  renewing:   '#f59e0b',
  n_a:        '#94a3b8',
};

export default function PermitsMap({
  projectId, permits, height = 500, onPermitClick,
}: {
  projectId: string;
  permits: Permit[];
  height?: number | string;
  onPermitClick?: (p: Permit) => void;
}) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<maplibregl.Map | null>(null);
  const [mapReady, setMapReady] = useState(false);

  // Split permits into point features vs line features.
  // Project-only permits (no geom) don't render on the map — they show up in
  // a floating "project-wide" pill in the corner.
  const { pointFc, lineFc, projectOnlyCount } = useMemo(() => {
    const points: any[] = [];
    const lines: any[] = [];
    let projOnly = 0;
    for (const p of permits) {
      if (p.project_only) { projOnly++; continue; }
      if (!p.geom) continue;
      const feat = {
        type: 'Feature',
        id: p.id,
        geometry: p.geom,
        properties: { permit_id: p.id, status: p.status, kind: p.kind, bucket: p.bucket },
      };
      if (p.geom.type === 'LineString' || p.geom.type === 'MultiLineString') lines.push(feat);
      else points.push(feat);
    }
    return {
      pointFc:   { type: 'FeatureCollection', features: points },
      lineFc:    { type: 'FeatureCollection', features: lines },
      projectOnlyCount: projOnly,
    };
  }, [permits]);

  // Fetch as-built network so we can render the fiber backbone, poles, vaults,
  // and structures as a faded base layer underneath the permit pins. This gives
  // operators spatial context even when every permit is project-wide (BEAD-6
  // case) or before any geometry-anchored permits have been created yet.
  // Uses the same endpoint AsBuiltMap consumes on the As-built page.
  const { data: asBuiltGeo } = useQuery<any>({
    queryKey: ['as-built-geojson', projectId],
    queryFn: () => api.get(`/as-built/projects/${projectId}/geojson`),
    enabled: !!projectId,
    staleTime: 30_000,
  });

  // Fetch the per-crossing pin set — one row per physical waterway / road /
  // rail crossing, with its parent permit's kind+status for coloring.
  const { data: crossingsData } = useQuery<{ items: any[] }>({
    queryKey: ['permit-crossings', projectId],
    queryFn: () => api.get(`/permits/crossings?project_id=${projectId}`),
    enabled: !!projectId,
    staleTime: 10_000,
  });

  // Split as-built features by geometry. The endpoint returns a STRUCTURED
  // object — { segments: FeatureCollection, poles: FC, vaults: FC, drops: FC,
  // closures: FC, structures: FC, equipment: FC, ducts: FC, plan_lanes: [] }
  // — not a flat FeatureCollection. We flatten + bucket by geometry type:
  // line-features (segments, ducts) become the faded line layer; point-
  // features (poles, vaults, closures, drops, structures, equipment) become
  // the faded point layer. Detail differentiation stays on the As-built page.
  const { asBuiltLineFc, asBuiltPointFc } = useMemo(() => {
    const lines: any[] = [];
    const points: any[] = [];
    const ab = asBuiltGeo as any;
    if (ab && typeof ab === 'object') {
      const lineKinds  = ['segments', 'ducts'];
      const pointKinds = ['poles', 'vaults', 'closures', 'drops', 'structures', 'equipment'];
      for (const k of lineKinds) {
        for (const f of (ab[k]?.features ?? [])) {
          if (f?.geometry) lines.push(f);
        }
      }
      for (const k of pointKinds) {
        for (const f of (ab[k]?.features ?? [])) {
          if (f?.geometry) points.push(f);
        }
      }
    }
    return {
      asBuiltLineFc:  { type: 'FeatureCollection', features: lines },
      asBuiltPointFc: { type: 'FeatureCollection', features: points },
    };
  }, [asBuiltGeo]);

  const hasAsBuilt = asBuiltLineFc.features.length > 0 || asBuiltPointFc.features.length > 0;

  // Build a FeatureCollection of crossing pins. Each gets its parent permit's
  // status + kind in properties so MapLibre can color-by-status and the click
  // handler can route the user back to the master permit.
  const crossingFc = useMemo(() => {
    const items = (crossingsData?.items ?? []) as any[];
    const features = items
      .filter(x => x.geom && x.geom.type === 'Point')
      .map(x => ({
        type: 'Feature',
        id: x.id,
        geometry: x.geom,
        properties: {
          permit_id:      x.permit_id,
          permit_kind:    x.permit_kind,
          permit_status:  x.permit_status,
          permit_bucket:  x.permit_bucket,
          crossing_kind:  x.crossing_kind,
          feature_name:   x.feature_name,
          feature_owner:  x.feature_owner,
        },
      }));
    return { type: 'FeatureCollection', features } as any;
  }, [crossingsData]);
  const hasCrossings = crossingFc.features.length > 0;

  // Init map
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const map = new maplibregl.Map({
      container: containerRef.current,
      style: {
        version: 8,
        sources: { 'osm': { type: 'raster', tiles: ['https://tile.openstreetmap.org/{z}/{x}/{y}.png'], tileSize: 256 } },
        layers: [{ id: 'osm-layer', type: 'raster', source: 'osm' }],
      },
      center: [-80, 38],
      zoom: 6,
    });
    mapRef.current = map;
    map.on('load', () => setMapReady(true));
    return () => { map.remove(); mapRef.current = null; };
  }, []);

  // Render data
  useEffect(() => {
    const m = mapRef.current;
    if (!m || !mapReady) return;
    const map = m;

    function upsert(id: string, fc: any) {
      const ex = map.getSource(id) as maplibregl.GeoJSONSource | undefined;
      if (ex) ex.setData(fc);
      else map.addSource(id, { type: 'geojson', data: fc });
    }

    // As-built base layer first so it stacks UNDER the permit pins.
    upsert('asbuilt-ln-src', asBuiltLineFc);
    upsert('asbuilt-pt-src', asBuiltPointFc);

    if (!map.getLayer('asbuilt-line')) {
      map.addLayer({
        id: 'asbuilt-line',
        type: 'line',
        source: 'asbuilt-ln-src',
        paint: {
          // Slate-500 with high opacity — clearly visible against OSM but
          // still reads as secondary to colored permit pins on top.
          'line-color': '#64748b',
          'line-width': 3,
          'line-opacity': 0.9,
        },
      });
    }
    if (!map.getLayer('asbuilt-point')) {
      map.addLayer({
        id: 'asbuilt-point',
        type: 'circle',
        source: 'asbuilt-pt-src',
        paint: {
          'circle-radius': 4,
          'circle-color': '#475569',
          'circle-stroke-color': '#fff',
          'circle-stroke-width': 1.5,
          'circle-opacity': 0.95,
        },
      });
    }

    upsert('permit-pt-src', pointFc);
    upsert('permit-ln-src', lineFc);
    upsert('crossing-src',  crossingFc);

    // Build color match expression by status
    const colorExpr: any[] = ['match', ['get', 'status']];
    for (const [status, color] of Object.entries(STATUS_COLOR)) {
      colorExpr.push(status, color);
    }
    colorExpr.push('#94a3b8'); // fallback

    // Same expression but reads `permit_status` from the crossing layer.
    const crossingColorExpr: any[] = ['match', ['get', 'permit_status']];
    for (const [status, color] of Object.entries(STATUS_COLOR)) {
      crossingColorExpr.push(status, color);
    }
    crossingColorExpr.push('#94a3b8');

    // Different glyph per crossing kind. We render all as circles for now
    // but vary the stroke color so waterways read as blue-ringed, highways
    // amber-ringed, local roads gray, railroads black.
    const crossingStrokeExpr: any[] = ['match', ['get', 'crossing_kind'],
      'waterway',  '#1d4ed8',
      'highway',   '#b45309',
      'local_road','#475569',
      'railroad',  '#111827',
      '#ffffff',
    ];

    if (!map.getLayer('permit-line')) {
      map.addLayer({
        id: 'permit-line',
        type: 'line',
        source: 'permit-ln-src',
        paint: {
          'line-color': colorExpr as any,
          'line-width': 4,
          'line-opacity': 0.8,
        },
      });
    }
    if (!map.getLayer('permit-point')) {
      map.addLayer({
        id: 'permit-point',
        type: 'circle',
        source: 'permit-pt-src',
        paint: {
          'circle-radius': 8,
          'circle-color': colorExpr as any,
          'circle-stroke-color': '#fff',
          'circle-stroke-width': 2,
        },
      });
    }

    // Crossing pins — small dots, ringed by crossing-kind color, filled by
    // parent permit's status. One pin per physical crossing; clicking opens
    // the parent master permit.
    if (!map.getLayer('crossing-point')) {
      map.addLayer({
        id: 'crossing-point',
        type: 'circle',
        source: 'crossing-src',
        paint: {
          'circle-radius': 5,
          'circle-color': crossingColorExpr as any,
          'circle-stroke-color': crossingStrokeExpr as any,
          'circle-stroke-width': 2,
          'circle-opacity': 0.95,
        },
      });
    }

    // Fit to data on first load — include as-built so the map zooms to a
    // useful extent even when there are no permit pins yet.
    try {
      const bounds = new maplibregl.LngLatBounds();
      const allFeats = [
        ...pointFc.features,
        ...lineFc.features,
        ...crossingFc.features,
        ...asBuiltPointFc.features,
        ...asBuiltLineFc.features,
      ];
      for (const f of allFeats) {
        const g: any = f.geometry;
        if (!g) continue;
        if (g.type === 'Point') bounds.extend(g.coordinates);
        else if (g.type === 'LineString') for (const c of g.coordinates) bounds.extend(c);
        else if (g.type === 'MultiLineString') for (const part of g.coordinates) for (const c of part) bounds.extend(c);
      }
      if (!bounds.isEmpty()) map.fitBounds(bounds, { padding: 50, maxZoom: 17, duration: 0 });
    } catch {}

  }, [mapReady, pointFc, lineFc, asBuiltLineFc, asBuiltPointFc, crossingFc]);

  // Click handler
  useEffect(() => {
    const m = mapRef.current;
    if (!m || !mapReady) return;
    const onClick = (e: maplibregl.MapMouseEvent) => {
      const fs = m.queryRenderedFeatures(e.point, {
        layers: ['crossing-point', 'permit-point', 'permit-line'],
      });
      if (fs.length === 0) return;
      const f = fs[0] as any;
      const permitId = f.properties?.permit_id;
      const permit = permits.find(p => p.id === permitId);
      if (permit && onPermitClick) onPermitClick(permit);
    };
    const onEnter = () => { m.getCanvas().style.cursor = 'pointer'; };
    const onLeave = () => { m.getCanvas().style.cursor = ''; };
    m.on('click', onClick);
    for (const lid of ['crossing-point', 'permit-point', 'permit-line']) {
      m.on('mouseenter', lid, onEnter);
      m.on('mouseleave', lid, onLeave);
    }
    return () => {
      m.off('click', onClick);
      for (const lid of ['crossing-point', 'permit-point', 'permit-line']) {
        m.off('mouseenter', lid, onEnter);
        m.off('mouseleave', lid, onLeave);
      }
    };
  }, [mapReady, permits, onPermitClick]);

  const hasGeoFeatures = pointFc.features.length > 0 || lineFc.features.length > 0 || hasCrossings;

  // CRITICAL: always render the map container, even when we're going to overlay
  // an empty state on top. Returning a different JSX tree for the empty-state
  // branch was unmounting the container and causing the init useEffect (which
  // has [] deps and only runs on mount) to bail out on first paint before the
  // as-built query resolved. The result was a blank canvas once the data came
  // in but the effect never re-ran. The fix: keep the map container in the DOM
  // unconditionally and toggle the empty state as a sibling overlay.

  const showEmptyAllProjectWide = !hasGeoFeatures && !hasAsBuilt && projectOnlyCount > 0;
  const showEmptyNoPermits      = !hasGeoFeatures && !hasAsBuilt && projectOnlyCount === 0;

  return (
    <div style={{ position: 'relative', height, width: '100%', borderRadius: 6, overflow: 'hidden', border: '1px solid var(--border)' }}>
      <div ref={containerRef} style={{ height: '100%', width: '100%' }} />

      {/* Empty-state overlays — sit on top of the (possibly blank) map. Only
          one of these is ever visible at a time, and only when there is
          truly nothing geographic to render. */}
      {showEmptyAllProjectWide && (
        <div style={{
          position: 'absolute', inset: 0, zIndex: 20,
          background: '#f8fafc',
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          padding: 32, textAlign: 'center',
        }}>
          <div style={{ fontSize: 36, marginBottom: 8 }}>📋</div>
          <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', marginBottom: 4 }}>
            {projectOnlyCount} project-wide permit{projectOnlyCount !== 1 ? 's' : ''} — no map pins
          </div>
          <div style={{ fontSize: 12, color: '#475569', maxWidth: 480, lineHeight: 1.5 }}>
            NEPA / §106 / Tribal Consultation / ESA / MBTA / Floodplain cover the entire project,
            not specific points. They appear in the list below. Geometry-anchored permits
            (§404 stream crossings, building permits, pole attachments) will populate the map
            once you import as-built data with the right characteristics.
          </div>
        </div>
      )}

      {showEmptyNoPermits && (
        <div style={{
          position: 'absolute', inset: 0, zIndex: 20,
          background: '#f8fafc',
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          padding: 32, textAlign: 'center',
        }}>
          <div style={{ fontSize: 36, marginBottom: 8 }}>🗺</div>
          <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', marginBottom: 4 }}>
            No permits yet for this project
          </div>
          <div style={{ fontSize: 12, color: '#475569', maxWidth: 480, lineHeight: 1.5 }}>
            Click <b>↻ Re-detect</b> above to auto-create the BEAD-required permits and detect
            permits from any as-built data. Or use <b>+ Add permit</b> for manual entry.
          </div>
        </div>
      )}

      {/* Status legend */}
      <div style={{
        position: 'absolute', top: 12, right: 12, zIndex: 10,
        background: '#fff', border: '1px solid #e5e7eb', borderRadius: 6,
        boxShadow: '0 2px 8px rgba(0,0,0,0.12)', padding: '8px 12px',
        fontSize: 11, fontFamily: 'system-ui',
      }}>
        <div style={{ fontWeight: 700, marginBottom: 4, fontSize: 10, textTransform: 'uppercase', letterSpacing: '.05em', color: '#475569' }}>Status</div>
        {['needed', 'applied', 'in_review', 'issued', 'denied'].map(s => (
          <div key={s} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '2px 0' }}>
            <span style={{ width: 10, height: 10, background: STATUS_COLOR[s], borderRadius: '50%', display: 'inline-block' }} />
            <span style={{ textTransform: 'capitalize' }}>{s.replace('_', ' ')}</span>
          </div>
        ))}
      </div>

      {/* Project-wide pill (top-left) */}
      {projectOnlyCount > 0 && (
        <div style={{
          position: 'absolute', top: 12, left: 12, zIndex: 10,
          background: '#fff', border: '1px solid #e5e7eb', borderRadius: 6,
          padding: '8px 14px', fontSize: 12,
          boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
          maxWidth: 280,
        }}>
          <div style={{ fontWeight: 700, color: '#0f172a' }}>📋 {projectOnlyCount} project-wide</div>
          <div style={{ fontSize: 10, color: '#64748b', marginTop: 2 }}>NEPA / §106 / Tribal / ESA / MBTA / Floodplain — not map-pinned. See the list below.</div>
        </div>
      )}

      {/* As-built context hint — shown when the only thing on the map is the
          faded fiber network (no permit pins). Without this, the user sees
          grey lines and wonders what they are. */}
      {hasAsBuilt && !hasGeoFeatures && (
        <div style={{
          position: 'absolute', bottom: 12, left: 12, zIndex: 10,
          background: '#fff', border: '1px solid #e5e7eb', borderRadius: 6,
          padding: '6px 12px', fontSize: 11,
          boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
          maxWidth: 320,
        }}>
          <span style={{
            display: 'inline-block', width: 16, height: 2, background: '#94a3b8',
            verticalAlign: 'middle', marginRight: 6, opacity: 0.7,
          }} />
          <span style={{ color: '#475569' }}>
            Fiber network from <b>As-built</b> — no geometry-anchored permits yet.
          </span>
        </div>
      )}
    </div>
  );
}
